import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  Alert,
  AlertStatistics,
  AlertsState,
  AlertFilters,
  PaginationState,
} from "../interfaces/alerts";
import {
  fetchAlerts,
  fetchAlertStatistics,
  resolveAlert,
} from "./thunks";

const initialState: AlertsState = {
  alerts: [],
  statistics: null,
  filters: {},
  pagination: { page: 0, pageSize: 10, total: 0 },  // page debe empezar en 0
  loading: false,
  error: null,
  lastUpdated: null,
};

const alertsSlice = createSlice({
  name: "alerts",
  initialState,
  reducers: {
    // Filter and pagination actions
    setFiltersAction: (state, action: PayloadAction<AlertFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 0;  // Reset a página 0
    },
    setPaginationAction: (state, action: PayloadAction<PaginationState>) => {
      state.pagination = action.payload;
    },
    clearErrorAction: (state) => {
      state.error = null;
    },

    // Clear all alerts
    alertsClearAction: (state) => {
      state.alerts = [];
      state.statistics = null;
      state.filters = {};
      state.pagination = { page: 0, pageSize: 10, total: 0 };  // page debe empezar en 0
      state.loading = false;
      state.error = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch alerts
    builder.addCase(fetchAlerts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAlerts.fulfilled, (state, action) => {
      state.alerts = action.payload.alerts;
      state.pagination.total = action.payload.total;
      state.loading = false;
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchAlerts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch statistics
    builder.addCase(fetchAlertStatistics.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAlertStatistics.fulfilled, (state, action) => {
      state.statistics = action.payload;
      state.loading = false;
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(fetchAlertStatistics.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Resolve alert
    builder.addCase(resolveAlert.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resolveAlert.fulfilled, (state, action) => {
      const alertId = action.payload;
      state.alerts = state.alerts.filter((alert) => alert.id !== alertId);
      state.pagination.total = Math.max(0, state.pagination.total - 1);
      state.loading = false;
      state.lastUpdated = new Date().toISOString();
    });
    builder.addCase(resolveAlert.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const {
  setFiltersAction,
  setPaginationAction,
  clearErrorAction,
  alertsClearAction,
} = alertsSlice.actions;

export default alertsSlice.reducer;
